import * as React from 'react';
import { Modal, Timeline } from 'antd';
import { connect } from 'react-redux';
import DateTimeHover from '../date_time_component/DateTime';
import {
	getWorkOrderTimeZoneId,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { clampCheckOutToCheckIn, getTimeDuration } from '../../utils/DataFormatterUtils';
import { ProtectedImageThumbnails } from '../protected_image_thumbnails/ProtectedImageThumbnails';
import { UserName } from '../name_component/user/UserNameComponent';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import AudioPlayer from '../MessageBubble/AudioPlayer';
import { formatRevisionInfo } from '../invoice_utilities/InvoiceUtilities';
import { wasRevisionSectionEdited } from '../../utils/ServiceCallRevisionUtils';

const HEADER_FORMAT = 'h:mm a z on MMM D';

const notesBlurbStyle: React.CSSProperties = {
	maxWidth: 480,
	marginTop: 8,
	background: '#fafafa',
	color: 'rgba(0, 0, 0, 0.65)',
	borderRadius: 30,
	padding: '8px 16px',
	width: 'fit-content',
};

const findContactForEmail = (serviceCall: any, email?: string) => {
	if (!email) return null;
	const candidates = [
		nullSafeGet('checkOutByContact', serviceCall),
		nullSafeGet('checkInByContact', serviceCall),
		nullSafeGet('leadTechnicianEmailContact', serviceCall),
	];
	return candidates.find((contact) => contact && contact.email === email) || null;
};

const buildRevisionLines = (
	snapshot: any,
	tz: string,
	userType: string,
	revision: any,
	allRevisions: any[]
) => {
	const checkInEdited = wasRevisionSectionEdited(allRevisions, revision.revision, 'checkIn');
	const checkOutEdited = wasRevisionSectionEdited(allRevisions, revision.revision, 'checkOut');
	const scheduledLine = snapshot.serviceScheduledAt ? (
		<div style={{ fontSize: 16 }}>
			Scheduled to arrive by{' '}
			<DateTimeHover
				timestamp={snapshot.serviceScheduledAt}
				dateFormat={HEADER_FORMAT}
				showDate
				parseZone={false}
				timeZone={tz}
			/>
			.
		</div>
	) : null;

	const checkInLine = snapshot.checkInTime ? (
		<div style={{ fontSize: 16 }}>
			<div>
				Started work at{' '}
				<DateTimeHover
					timestamp={snapshot.checkInTime}
					dateFormat={HEADER_FORMAT}
					showDate
					timeZone={tz}
				/>
				.
				{checkInEdited ? (
					<span style={{ color: 'rgba(0,0,0,0.45)', fontStyle: 'italic' }}> (edited)</span>
				) : null}
			</div>
			{snapshot.checkInAudio ? (
				<div style={notesBlurbStyle}>
					<div>
						<AudioPlayer type="default" src={snapshot.checkInAudio} />
					</div>
					{snapshot.checkInAudioTranscription ? (
						<div>Transcription: {snapshot.checkInAudioTranscription}</div>
					) : null}
				</div>
			) : null}
			{snapshot.checkInNotes && snapshot.checkInNotes.length > 0 ? (
				<div style={notesBlurbStyle}>{snapshot.checkInNotes}</div>
			) : null}
			{snapshot.checkInImages && snapshot.checkInImages.length > 0 ? (
				<ProtectedImageThumbnails
					style={{ marginTop: 16 }}
					imageWidth={64}
					imageHeight={64}
					photoStrings={snapshot.checkInImages}
					role={userType}
				/>
			) : null}
		</div>
	) : null;

	const checkOutLine = snapshot.checkOutTime ? (
		<div style={{ fontSize: 16 }}>
			<div>
				Ended work at{' '}
				<DateTimeHover
					timestamp={snapshot.checkOutTime}
					dateFormat={HEADER_FORMAT}
					showDate
					timeZone={tz}
				/>
				.
				{checkOutEdited ? (
					<span style={{ color: 'rgba(0,0,0,0.45)', fontStyle: 'italic' }}> (edited)</span>
				) : null}
			</div>
			{snapshot.checkOutAudio ? (
				<div style={notesBlurbStyle}>
					<div>
						<AudioPlayer type="default" src={snapshot.checkOutAudio} />
					</div>
					{snapshot.checkOutAudioTranscription ? (
						<div>Transcription: {snapshot.checkOutAudioTranscription}</div>
					) : null}
				</div>
			) : null}
			{snapshot.checkOutNotes && snapshot.checkOutNotes.length > 0 ? (
				<div style={notesBlurbStyle}>{snapshot.checkOutNotes}</div>
			) : null}
			{snapshot.checkOutImages && snapshot.checkOutImages.length > 0 ? (
				<ProtectedImageThumbnails
					style={{ marginTop: 16 }}
					imageWidth={64}
					imageHeight={64}
					photoStrings={snapshot.checkOutImages}
					role={userType}
				/>
			) : null}
		</div>
	) : null;

	const onSiteLine =
		snapshot.checkInTime && snapshot.checkOutTime ? (
			<div style={{ fontSize: 16 }}>
				On site for{' '}
				{getTimeDuration(
					clampCheckOutToCheckIn(snapshot.checkInTime, snapshot.checkOutTime),
					snapshot.checkInTime,
					{
						hideSeconds: true,
					}
				) || 'less than a minute'}
			</div>
		) : null;

	return [scheduledLine, checkInLine, checkOutLine, onSiteLine].filter((item) => item !== null);
};

interface Props {
	visible: boolean;
	onCancel: () => void;
	revisions: any[];
	serviceCall: any;
	workOrder: any;
	currentUser: any;
}

const ServiceCallRevisionViewerModalInner: React.FC<Props> = ({
	visible,
	onCancel,
	revisions,
	serviceCall,
	workOrder,
	currentUser,
}) => {
	const userType = nullSafeGet('userType', currentUser);
	const tz = getWorkOrderTimeZoneId(workOrder);
	const sorted = [...(revisions || [])].sort((left, right) => right.revision - left.revision);
	// The latest revision matches the current service call card; only show prior revisions.
	const priorRevisions = sorted.slice(1);

	return (
		<Modal
			visible={visible}
			width={640}
			title="Revision History"
			footer={null}
			onCancel={onCancel}
			closable
			maskClosable
			bodyStyle={{ maxHeight: '70vh', overflow: 'auto', padding: '0 24px' }}
		>
			{priorRevisions.length === 0 ? (
				<div style={{ color: 'rgba(0,0,0,0.45)' }}>No prior revisions to compare.</div>
			) : (
				priorRevisions.map((revision, idx) => {
					const snapshot = formatRevisionInfo(revision);
					const lines = buildRevisionLines(snapshot, tz, userType, revision, revisions);
					const revisedByContact = findContactForEmail(serviceCall, revision.revisedBy);
					const supplierName = nullSafeGet('supplierFacility.name', serviceCall);
					const leadTechContact = nullSafeGet('leadTechnicianEmailContact', serviceCall);
					return (
						<div key={revision.revision}>
							<div
								className="flex flex-wrap items-baseline gap-x-3"
								style={{
									background: '#F5F5F5',
									padding: '8px 24px',
									margin: '0 -24px',
								}}
							>
								<span className="h5medium">Revision {revision.revision}</span>
								<span className="footnoteregular" style={{ color: 'rgba(0,0,0,0.45)' }}>
									Edited{' '}
									{revisedByContact ? (
										<>
											by <UserName mode={'inline'} contact={revisedByContact} />{' '}
										</>
									) : revision.revisedBy ? (
										<>by {revision.revisedBy} </>
									) : null}
									at <DateTimeHover timestamp={revision.revisedAt} />
								</span>
							</div>
							<div style={{ paddingTop: 16 }}>
								{supplierName ? (
									<div
										className="serviceCallSupplierName"
										style={{
											marginBottom: 8,
											fontSize: '1.25rem',
											fontWeight: 400,
											letterSpacing: '0.009375rem',
										}}
									>
										{supplierName}
									</div>
								) : null}
								{leadTechContact ? (
									<div
										className="materialCard__primaryTitle"
										style={{
											marginBottom: 16,
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<ContactAvatar
											size="large"
											hidePopover={true}
											style={{ marginRight: 16 }}
											contact={leadTechContact}
											userType={userType}
										/>
										<div>
											<div className="materialCard__title">
												{nullSafeGet('nameGiven', leadTechContact)}{' '}
												{nullSafeGet('nameFamily', leadTechContact)}
											</div>
											<div className="materialCard__subtitle">
												{nullSafeGet('title', leadTechContact)}{' '}
												{nullSafeGet('department', leadTechContact)}
											</div>
										</div>
									</div>
								) : null}
								{lines.length === 0 ? (
									<div style={{ color: 'rgba(0,0,0,0.45)' }}>
										No timing fields recorded at this revision.
									</div>
								) : (
									<Timeline>
										{lines.map((line, lineIdx) => (
											<Timeline.Item key={lineIdx}>{line}</Timeline.Item>
										))}
									</Timeline>
								)}
							</div>
						</div>
					);
				})
			)}
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default connect(mapStateToProps)(ServiceCallRevisionViewerModalInner);
